import type { AmountDTO } from 'gw-edge-client/dist/types/dto/pc/edge/capabilities/currency/dto';
import type { LocalDateDTO } from 'gw-edge-client/dist/types/dto/pc/edge/time';
import type {
    AllCoverablesDTO,
    CoverableDTO,
    CoverageDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { MultipleFieldValueInformation } from '../config/flavourConfig';
import type { ConditionExclusionManualTextDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/common';
import { convertLocalDateDTOToDate, convertLocalDateToString } from './dateUtil';
import ConditionsAndExclusionsModal from '../shared_components/ConditionsAndExclusionsModal/ConditionsAndExclusionsModal';
import type {
    AllCoverablesOnsaDTO,
    CoverableOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import type {
    AreaOfDestination_Top,
    ProductName_Top
} from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { AllCoverables, ProductStatus } from '../recoil/types/onsaTypes';
import type {
    AdultAccidentOnsaDTO,
    ChildAccidentOnsaDTO,
    YoungAccidentOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/accident';
import type {
    PrivateContentOnsaDTO,
    YouthContentOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/content';
import type { CarOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/motor';
import type {
    PrhHolidayHomeOnsaDTO,
    PrhHouseOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/house';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import type { CoverageTextItemRequest } from '../recoil/types/hippoTypes';
import type {
    CaravanDTO,
    TrailerDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/uvp';
import { COVERABLE_CODE, FLAVOUR_NAME, FLAVOUR_NAME_MAP, LOB_CODE } from '../constants/coverable';
import type {
    CaravanOnsaDTO,
    TrailerOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/uvp';
import type { PrhCommonDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';
import { getKeyByValue } from './objectUtil';
const allCoverablesToCoverableDTOArray = (allCoverablesDTO: AllCoverablesDTO): CoverableDTO[] => {
    return Object.entries(allCoverablesDTO).flatMap((x) => x.pop());
};

const allCoverablesOnsaToCoverableDTOArray = (
    allCoverablesOnsaDTO: AllCoverablesOnsaDTO
): CoverableDTO[] => {
    return Object.entries(allCoverablesOnsaDTO).flatMap((x) => x.pop());
};

const isUnsupportedByEdi = (coverableDTO: CoverableDTO): boolean => {
    const unsupportedByEdiLobCodes = ['CHO'];
    return unsupportedByEdiLobCodes.includes(coverableDTO.lobCode ?? '');
};

const isAgricultureCoverable = (coverableDTO: CoverableDTO): boolean => {
    const agricultureLobCodes = ['CTP', 'PRA', 'WOC', 'MAC', 'LBV', 'CTV'];
    return agricultureLobCodes.includes(coverableDTO.lobCode ?? '');
};

const isShortTermCoverable = (coverableDTO: CoverableDTO): boolean => {
    const shortTermLobCodes = ['STT', 'STC', 'CHO'];
    return shortTermLobCodes.includes(coverableDTO.lobCode ?? '');
};

const isWocOrCtpCoverable = (coverableDTO: CoverableDTO): boolean => {
    const agricultureLobCodes = ['WOC', 'CTP'];
    return agricultureLobCodes.includes(coverableDTO.lobCode ?? '');
};

const isLocalDateDTO = (
    tableValue: LocalDateDTO | string | AmountDTO | MultipleFieldValueInformation[]
): tableValue is LocalDateDTO => {
    return (tableValue as LocalDateDTO).day !== undefined;
};

const isAmountDTO = (
    tableValue: LocalDateDTO | string | AmountDTO | MultipleFieldValueInformation[]
): tableValue is AmountDTO => {
    return (tableValue as AmountDTO).currency !== undefined;
};

const isMultipleFieldValueInformation = (
    tableValue: LocalDateDTO | string | AmountDTO | MultipleFieldValueInformation[]
): tableValue is MultipleFieldValueInformation[] => {
    return (tableValue as MultipleFieldValueInformation).modalInformation !== undefined;
};

const formatConditionsAndExclusionsToFieldValues = (
    conditionAndExclusionList: ConditionExclusionManualTextDTO[]
): MultipleFieldValueInformation[] => {
    return conditionAndExclusionList.map((condition) => {
        return {
            modalInformation: {
                modalType: ConditionsAndExclusionsModal,
                modalProps: {
                    title: condition.name ?? '',
                    description: condition.text ?? ''
                }
            },
            title: condition.name ?? ''
        };
    });
};

const validateUVPRegNumber = (regNumber: string): boolean => {
    return /^([A-Za-z]{2})(\d{4})$/.test(regNumber);
};

const isMonetary = (value: string): boolean => {
    return value.includes('kr');
};

const formatStringToMonetaryValueString = (value: string): string => {
    if (isMonetary(value)) return value;
    return Number(value).toLocaleString('da-DK') + ' kr.';
};

const formatCoverableFieldValue = (
    fieldValue: LocalDateDTO | AmountDTO | string | MultipleFieldValueInformation[] | boolean
): string => {
    if (typeof fieldValue === 'boolean') {
        return fieldValue ? 'Ja' : 'Nej';
    }
    if (isLocalDateDTO(fieldValue)) {
        const date = convertLocalDateDTOToDate(fieldValue);
        return convertLocalDateToString(date) ?? '';
    }
    if (isAmountDTO(fieldValue)) {
        const price = fieldValue.amount?.toFixed(2).toString();
        return price?.concat(' kr.').replace('.', ',') ?? '';
    }
    if (isMultipleFieldValueInformation(fieldValue)) {
        return '';
    }

    if (!fieldValue.includes('%') && fieldValue.includes('kr')) {
        const numberString = fieldValue.split(' ')[0];
        const numberStringWithoutDecimals = numberString.split(',')[0];
        const rawNumberString = numberStringWithoutDecimals.replace(/\./g, '');
        if (!isNaN(Number(rawNumberString))) {
            return formatStringToMonetaryValueString(rawNumberString);
        }
    }

    return fieldValue;
};

const getCoverableIndex = (
    coverables: CoverableOnsaDTO[] | CoverableDTO[],
    fixedId: string
): number => {
    return coverables?.findIndex((coverable) => coverable.fixedId === fixedId) ?? -1;
};

const getPropertyToUpdate = (flavourName: string): string => {
    switch (flavourName) {
        case FLAVOUR_NAME.ACC_ACCIDENT:
            return 'adultAccidents';
        case FLAVOUR_NAME.ACC_YOUNG:
            return 'youngAccidents';
        case FLAVOUR_NAME.ACC_CHILD:
            return 'childAccidents';
        case FLAVOUR_NAME.CAT:
            return 'cats';
        case FLAVOUR_NAME.DOG:
            return 'dogs';
        case FLAVOUR_NAME.PRC_CONTENT:
            return 'privateContent';
        case FLAVOUR_NAME.PRC_YOUTH_CONTENT:
            return 'youthContent';
        case FLAVOUR_NAME.TRA_HOUSEHOLD:
        case FLAVOUR_NAME.TRA_INDIVIDUAL:
            return 'travels';
        case FLAVOUR_NAME.MVP_CAR:
            return 'cars';
        case 'Ejendomsforsikring':
            return 'agricultureProperty';
        case 'Privat løsøre- og ansvarsforsikring':
            return 'agricultureContent';
        case 'Maskinforsikring':
            return 'machinery';
        case 'Arbejdsskadeforsikring':
            return 'workersComp';
        case FLAVOUR_NAME.KENNEL:
            return 'kennels';
        case FLAVOUR_NAME.STT_SINGLE_TRIP_TRAVEL:
            return 'singleTripTravel';
        case FLAVOUR_NAME.STC_SINGLE_TRIP_CANCELLATION:
            return 'singleTripCancellation';
        case FLAVOUR_NAME.PRH_HOUSE:
            return 'houses';
        case FLAVOUR_NAME.PRH_HOLIDAY_HOME:
            return 'holidayHomes';
        case FLAVOUR_NAME.UVP_CARAVAN:
            return 'caravans';
        case FLAVOUR_NAME.UVP_TRAILER:
            return 'trailers';
        default:
            return '';
    }
};

const getLobToUpdate = (flavourName: string): string => {
    switch (flavourName) {
        case 'Ulykkesforsikring':
            return 'adultAccident';
        case 'Ulykkesforsikring til unge':
            return 'youngAccident';
        case 'Børneforsikring':
            return 'childAccident';
        case 'Katteforsikring':
            return 'cat';
        case 'Hundeforsikring':
            return 'dog';
        case 'Indboforsikring':
            return 'privateContent';
        case 'Indboforsikring til unge':
            return 'youthContent';
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return 'travel';
        case 'Bilforsikring':
            return 'car';
        case 'Campingvognsforsikring':
            return 'caravan';
        case 'Påhængsvognsforsikring':
            return 'trailer';
        case 'Husforsikring':
            return 'house';
        case 'Fritidshusforsikring':
            return 'holidayHome';
        default:
            return '';
    }
};

const getCoveragePackageFieldsToUpdate = (coverable: CoverableOnsaDTO): object | undefined => {
    switch (coverable.flavourName) {
        case FLAVOUR_NAME.ACC_ACCIDENT:
            return { insuranceSum: (coverable as AdultAccidentOnsaDTO).insuranceSum };
        case FLAVOUR_NAME.ACC_YOUNG:
            return { insuranceSum: (coverable as YoungAccidentOnsaDTO).insuranceSum };
        case FLAVOUR_NAME.ACC_CHILD:
            return { insuranceSum: (coverable as ChildAccidentOnsaDTO).insuranceSum };
        case FLAVOUR_NAME.PRC_CONTENT:
            return {
                contentAndBaggageCovDeductible: (coverable as PrivateContentOnsaDTO)
                    .contentAndBaggageCovDeductible
            };
        case FLAVOUR_NAME.PRC_YOUTH_CONTENT:
            return {
                contentAndBaggageCovDeductible: (coverable as YouthContentOnsaDTO)
                    .contentAndBaggageCovDeductible
            };
        case FLAVOUR_NAME.MVP_CAR:
            return { deductible: (coverable as CarOnsaDTO).deductible };
        case FLAVOUR_NAME.UVP_CARAVAN:
            return {
                deductible: (coverable as CaravanOnsaDTO).deductible,
                contentSum: (coverable as CaravanOnsaDTO).contentSum
            };
        case FLAVOUR_NAME.UVP_TRAILER:
            return { deductible: (coverable as TrailerOnsaDTO).deductible };
        case FLAVOUR_NAME.PRH_HOUSE:
            return { fireCovDeductible: (coverable as PrhHouseOnsaDTO).fireCovDeductible };
        case FLAVOUR_NAME.PRH_HOLIDAY_HOME:
            return {
                fireCovDeductible: (coverable as PrhHolidayHomeOnsaDTO).fireCovDeductible,
                contentInsuranceSum: (coverable as PrhHolidayHomeOnsaDTO).contentInsuranceSum,
                contentInsuranceManualSum: (coverable as PrhHolidayHomeOnsaDTO)
                    .contentInsuranceManualSum
            };
        default:
            return undefined;
    }
};

const OnsaflavourOrder: string[] = [
    'privateContent',
    'youthContent',
    'travels',
    'cars',
    'caravans',
    'trailers',
    'adultAccidents',
    'youngAccidents',
    'childAccidents',
    'dogs',
    'cats'
];

const getCurrentProduct = (coverableList: Record<string, ProductStatus[]>): ProductStatus => {
    const sortedList = Object.fromEntries(
        Object.entries(coverableList).sort(
            ([a], [b]) => OnsaflavourOrder.indexOf(a) - OnsaflavourOrder.indexOf(b)
        )
    );

    const currentProduct = Object.values(sortedList)
        .flatMap((productList) => productList)
        .find((item) => !item.calculated) as ProductStatus;

    return currentProduct;
};

const getOnsaCoverable = (
    coverables: AllCoverablesOnsaDTO,
    flavourName: string,
    fixedId: string
): CoverableOnsaDTO => {
    const propertyToUpdate = getPropertyToUpdate(flavourName);
    const flavour: CoverableOnsaDTO[] =
        coverables[propertyToUpdate as keyof AllCoverablesOnsaDTO] ?? [];
    const coverable = flavour?.find((coverable) => coverable.fixedId === fixedId);
    if (!coverable) {
        return {
            fixedId: '',
            flavourCode: '',
            flavourName: '',
            lobCode: '',
            publicId: '',
            termIds: [],
            isAddedInOnsa: false
        };
    }
    return coverable;
};

const areaDestination: Record<AreaOfDestination_Top, string> = {
    Worldwide: 'Worldwide',
    OnlyEurope: 'OnlyEurope'
} as const;

const getCoverageFromCoverable = (
    coverages: CoverageDTO[] | undefined,
    coverageType: string
): CoverageDTO | undefined => {
    return coverages?.find((coverage) => coverage.type === coverageType);
};

const getCoverableFromAllOnsaCoverables = (
    coverables: AllCoverablesOnsaDTO,
    fixedId: string,
    lobCode: string
): CoverableDTO | undefined => {
    return allCoverablesOnsaToCoverableDTOArray(coverables).find(
        (cov) => cov.fixedId === fixedId && cov.lobCode === lobCode
    );
};

const shouldImmediatelyWithdrawJobForProduct = (flavourName: string): boolean => {
    switch (flavourName) {
        case 'Indboforsikring':
        case 'Indboforsikring til unge':
        case 'Rejseforsikring':
        case 'Rejseforsikring til enkeltperson':
            return true;
        default:
            return false;
    }
};

const updateProductStatusList = (
    productStatusList: Record<string, ProductStatus[]>,
    allCoverables: AllCoverables,
    isCalculated = false
): Record<string, ProductStatus[]> => {
    const updatedList: Record<string, ProductStatus[]> = { ...productStatusList };

    for (const property in allCoverables) {
        const coverables = allCoverables[property as keyof AllCoverables];

        if (Array.isArray(coverables) && coverables.length > 0) {
            const newStatuses = [...coverables]
                .sort(
                    (a, b) =>
                        (Object.entries(b).length - Object.entries(a).length ||
                            a.fixedId?.localeCompare(b.fixedId ?? '')) ??
                        0
                )
                .map((coverable) => ({
                    calculated: isCalculated,
                    fixedId: coverable.fixedId ?? '',
                    flavourName: coverable.flavourName ?? '',
                    lobCode: coverable.lobCode ?? '',
                    flavourCode: coverable.flavourCode ?? ''
                }));
            updatedList[property] = [...(updatedList[property] ?? []), ...newStatuses];
        }
    }
    return updatedList;
};

const formatMonetaryAmountToStringWithoutCurrency = (value: number): string => {
    return value.toLocaleString('da-DK');
};

const shouldShowConditionReportWarning = (coverable: CoverableDTO): boolean => {
    if (coverable.lobCode !== LOB_CODE.PRIVATE_HOUSE) return false;
    return (
        'outputBuildingReportPending' in coverable &&
        typeof coverable.outputBuildingReportPending === 'boolean' &&
        coverable.outputBuildingReportPending
    );
};

const isAccCoverableForOtherPerson = (coverable: CoverableDTO): boolean => {
    return (
        coverable.lobCode === 'ACC' &&
        'isForOtherPerson' in coverable &&
        !!coverable.isForOtherPerson
    );
};

const findCarWithMatchingProperty = (
    shoppingBasketCoverables: AllCoverablesDTO,
    onsaCoverables: AllCoverablesOnsaDTO,
    currentCarFixedId: string,
    carProperty: keyof CarDTO & keyof CarOnsaDTO
): CarOnsaDTO | CarDTO | undefined => {
    const carInShoppingBasket = shoppingBasketCoverables.cars?.find(
        (car) => car[carProperty] !== undefined
    );
    const carInOnsa = onsaCoverables.cars?.find(
        (car) => car.fixedId !== currentCarFixedId && car[carProperty] !== undefined
    );

    return carInShoppingBasket || carInOnsa;
};

const getCoverableByIdAndFlavourName = (
    coverables: CoverableDTO[],
    fixedId: string,
    flavourName: string
): CoverableDTO | undefined => {
    return coverables.find(
        (coverable) => coverable.fixedId === fixedId && coverable.flavourName === flavourName
    );
};

const getCoverableByIdAndFlavourCode = (
    coverables: CoverableDTO[],
    fixedId: string,
    flavourCode: string
): CoverableDTO | undefined => {
    return coverables.find((coverable) => {
        return isMatchingCoverable(coverable, fixedId, flavourCode);
    });
};

const isMatchingCoverable = (
    coverable: CoverableDTO,
    fixedId: string,
    flavourCode: string
): boolean => {
    return (
        coverable.fixedId === fixedId &&
        (coverable.flavourCode === flavourCode || coverable.flavourName === flavourCode)
    );
};

const getCoverageRequestItems = (
    termIds: string[],
    coverageIds: string[]
): CoverageTextItemRequest[] => {
    return termIds.flatMap((termsId) =>
        coverageIds.map((coverageId) => ({
            termsId,
            coverageId
        }))
    );
};

const getCoverableById = (
    allCoverablesDTO: AllCoverablesDTO,
    fixedId: string
): CoverableDTO | undefined => {
    const coverables = allCoverablesToCoverableDTOArray(allCoverablesDTO);
    return coverables.find((coverable) => coverable.fixedId === fixedId);
};

const shouldShowUvpWarning = (coverable: CaravanDTO | TrailerDTO): boolean => {
    if (coverable.flavourName === FLAVOUR_NAME.UVP_CARAVAN) {
        return !coverable.stationary && !coverable.regNumber;
    }
    if (coverable.flavourName === FLAVOUR_NAME.UVP_TRAILER) {
        return !coverable.regNumber;
    }
    return false;
};

const doesCoverablesContainChangedStatus = (coverables: CoverableDTO[]): boolean => {
    return coverables.some((coverable) => coverable.status === 'Changed');
};

const isPrivateContentLiabilityOnly = (coverable: CoverableDTO): boolean => {
    return (
        coverable.flavourName === FLAVOUR_NAME.PRC_CONTENT &&
        coverable.coverages?.length === 1 &&
        coverable.coverages[0]?.type === 'PRCLiabilityCov'
    );
};

const getEarliestEffectiveCoverable = (
    coverables: Array<CoverableDTO | undefined>
): CoverableDTO | undefined => {
    const earliestEffectiveCoverable = coverables.reduce((earliestCov, currentCov) => {
        if (!earliestCov || !currentCov) {
            return earliestCov || currentCov;
        }
        const earliestCovEffDate = convertLocalDateDTOToDate(earliestCov.effectiveDate ?? {});
        const currentCovEffDate = convertLocalDateDTOToDate(currentCov.effectiveDate ?? {});
        if (currentCovEffDate < earliestCovEffDate) {
            return currentCov;
        }
        return earliestCov;
    });
    return earliestEffectiveCoverable;
};

const isAllPRHCoverablesWithReportOrUWI = (coverables: CoverableDTO[]): boolean => {
    return coverables.every(
        (coverable) =>
            coverable.lobCode === LOB_CODE.PRIVATE_HOUSE &&
            ((coverable as PrhCommonDTO)?.outputBuildingReportPending ||
                (coverable as PrhCommonDTO)?.hasUWI)
    );
};

const getAgeOfTheOldestFromCoverableList = (
    coverables: AllCoverablesOnsaDTO
): number | undefined => {
    for (const coverable of allCoverablesOnsaToCoverableDTOArray(coverables)) {
        if (!coverable.yearlyPrice && !coverable.monthlyPrice) {
            continue;
        }
        switch (coverable.flavourCode) {
            case COVERABLE_CODE.prhHouse:
                if ((coverable as PrhHouseOnsaDTO).ageOfOldestInHousehold) {
                    return (coverable as PrhHouseOnsaDTO).ageOfOldestInHousehold;
                }
                break;
            case COVERABLE_CODE.prhHolidayHome:
                if ((coverable as PrhHolidayHomeOnsaDTO).ageOfOldestInHousehold) {
                    return (coverable as PrhHouseOnsaDTO).ageOfOldestInHousehold;
                }
                break;
            case COVERABLE_CODE.privateContent:
                if ((coverable as PrivateContentOnsaDTO).ageOfOldestInHousehold) {
                    return (coverable as PrivateContentOnsaDTO).ageOfOldestInHousehold;
                }
                break;
            case COVERABLE_CODE.youthContent:
                if ((coverable as YouthContentOnsaDTO).ageOfOldestInHousehold) {
                    return (coverable as YouthContentOnsaDTO).ageOfOldestInHousehold;
                }
                break;
            case COVERABLE_CODE.caravan:
                if ((coverable as CaravanOnsaDTO).ageOfOldestInHousehold) {
                    return (coverable as CaravanOnsaDTO).ageOfOldestInHousehold;
                }
                break;
            case COVERABLE_CODE.trailer:
                if ((coverable as TrailerOnsaDTO).ageOfOldestInHousehold) {
                    return (coverable as TrailerOnsaDTO).ageOfOldestInHousehold;
                }
                break;
            case COVERABLE_CODE.car:
                if ((coverable as CarOnsaDTO).ageOfOldestInHousehold) {
                    return (coverable as CarOnsaDTO).ageOfOldestInHousehold;
                }
                break;
        }
    }
    return undefined;
};

const getProductName = (flavourName: string): ProductName_Top =>
    getKeyByValue(FLAVOUR_NAME_MAP, flavourName?.toLowerCase() ?? '') as ProductName_Top;

export {
    allCoverablesToCoverableDTOArray,
    allCoverablesOnsaToCoverableDTOArray,
    isAgricultureCoverable,
    isShortTermCoverable,
    formatCoverableFieldValue,
    formatConditionsAndExclusionsToFieldValues,
    formatStringToMonetaryValueString,
    getCoverableIndex,
    getPropertyToUpdate,
    getOnsaCoverable,
    areaDestination,
    getCoverageFromCoverable,
    isWocOrCtpCoverable,
    getCurrentProduct,
    getCoverableFromAllOnsaCoverables,
    getLobToUpdate,
    isUnsupportedByEdi,
    getCoveragePackageFieldsToUpdate,
    shouldImmediatelyWithdrawJobForProduct,
    updateProductStatusList,
    formatMonetaryAmountToStringWithoutCurrency,
    isAccCoverableForOtherPerson,
    findCarWithMatchingProperty,
    shouldShowConditionReportWarning,
    getCoverageRequestItems,
    getCoverableByIdAndFlavourCode,
    getCoverableByIdAndFlavourName,
    getCoverableById,
    shouldShowUvpWarning,
    validateUVPRegNumber,
    doesCoverablesContainChangedStatus,
    isPrivateContentLiabilityOnly,
    getEarliestEffectiveCoverable,
    getProductName,
    isAllPRHCoverablesWithReportOrUWI,
    getAgeOfTheOldestFromCoverableList
};
